import Link from 'next/link'
import styled from 'styled-components'

import { theme } from '@/styles/theme'
import { formatDate } from '@/utils/date'

import { BlogTag } from '@/components/elements/Blog/BlogTag'
import { Interviewee } from '@/components/elements/Blog/Interviewee'

import { useMediaQuery } from '@/hooks/useMediaQuery'
import type { Blog } from '@/types/blog'

export const BlogItem = ({
  _id,
  coverImage,
  title,
  tag,
  publishDate,
  interviewee,
}: Blog) => {
  const isMobile = useMediaQuery('(max-width: 1047px)')
  const cover =
    isMobile && coverImage.spCoverImage
      ? coverImage.spCoverImage
      : coverImage.pcCoverImage

  return (
    <Article>
      <Link href={`/news/blog/${_id}`} passHref>
        <a>
          <CoverImage>
            <img
              src={cover.src}
              height="100%"
              width="100%"
              alt={cover.altText}
            />
          </CoverImage>
          <Content>
            <div>
              <Date dateTime={publishDate}>
                {formatDate(publishDate, 'yyyy.MM.dd')}
              </Date>
              <Title>{title}</Title>
            </div>
            <div>
              <TagWrapper>
                {tag.map((tagItem) => (
                  <BlogTag key={tagItem._id} {...tagItem} />
                ))}
              </TagWrapper>
              <Interviewee {...interviewee} />
            </div>
          </Content>
        </a>
      </Link>
    </Article>
  )
}

const Article = styled.article`
  width: 316px;
  height: 447px;
  transition: 0.3s;

  &:hover {
    > a > div > img {
      opacity: 0.6;
    }

    time,
    h3 {
      color: ${theme.colors.hoverLightBlue};
    }
  }

  @media screen and (max-width: 1047px) {
    width: 100%;
    height: 451px;
  }
`

const CoverImage = styled.div`
  height: 188px;

  > img {
    border-radius: 8px 8px 0 0;
    object-fit: cover;
  }

  @media screen and (max-width: 1047px) {
    height: 208px;
  }
`

const Content = styled.div`
  height: calc(100% - 188px);
  padding: 19px 25px 26px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: #ffffff;
  border-style: solid;
  border-color: ${theme.colors.backgroundPaleBlue};
  border-width: 0 2px 2px 2px;
  border-radius: 0 0 8px 8px;

  > div {
    overflow: hidden;
  }

  @media screen and (max-width: 1047px) {
    height: calc(100% - 208px);
    padding: 18px 22px 24px;
  }
`

const Date = styled.time`
  color: ${theme.colors.textSecond};
  font-size: 12px;
  letter-spacing: 1.2px;
`

const Title = styled.h3`
  font-size: 18px;
  line-height: 24px;
  letter-spacing: 1.8px;
  color: ${theme.colors.textTitle};
  font-weight: bold;
  height: 74px;
  margin-top: 9px;

  @media screen and (max-width: 1047px) {
    font-size: 16px;
    letter-spacing: 1.6px;
    margin-top: 8px;
    height: 72px;
  }
`

const TagWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 19px;
`
